import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { ButtonModule } from 'primeng/button'
import { SkeletonModule } from 'primeng/skeleton'
import { ConfirmationService, MessageService } from 'primeng/api'
import { AtomicDelivery, AtomicDeliveryConflictError, AtomicDeliveryStatus, SyncAuditService } from '@/service/sync-audit.service'
import { SyncAuditEntry } from '@/domain/sync-audit'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { TableModule } from 'primeng/table'

interface Row extends AtomicDelivery { busy: boolean; retrying: boolean; requestPayload?: unknown }

@Component({
  selector: 'sync-quarantine', standalone: true,
  imports: [CommonModule, ButtonModule, SkeletonModule, ConfirmDialogModule, TableModule], templateUrl: './sync-quarantine.component.html', styleUrl: './sync-quarantine.component.scss',
})
export class SyncQuarantineComponent implements OnInit, OnDestroy {
  loading = true; error: string | null = null; rows: Row[] = []
  private hasLoaded = false; private destroyed = false; private readonly abort = new AbortController(); private refreshTimer: ReturnType<typeof setInterval> | null = null
  constructor(private readonly service: SyncAuditService, private readonly messages: MessageService, private readonly confirmation: ConfirmationService) {}
  ngOnInit(): void { void this.refresh(); this.refreshTimer = setInterval(() => void this.refresh(), 10_000) }
  ngOnDestroy(): void { this.destroyed = true; this.abort.abort(); if (this.refreshTimer) clearInterval(this.refreshTimer); this.service.cancelAllRequests() }
  async refresh(): Promise<void> {
    this.loading = !this.hasLoaded; this.error = null
    try {
      const [live, summaries] = await Promise.allSettled([
        this.service.listAtomicDeliveries({ signal: this.abort.signal }),
        this.service.loadSalesforceQuarantines({ signal: this.abort.signal }),
      ])
      if (live.status === 'rejected') throw live.reason
      const payloads = this.payloadsByDelivery(summaries.status === 'fulfilled' ? summaries.value : [])
      if (!this.destroyed) this.rows = live.value.map((delivery) => ({
        ...delivery, busy: false, retrying: false,
        requestPayload: delivery.requestPayload ?? payloads.get(this.key(delivery)),
      }))
      // The tab shows one row per delivery, so its badge must use the same unit.
      this.service.setQuarantineEntityCount(live.value.length)
    } catch (error) { if (!this.destroyed) this.error = error instanceof Error ? error.message : 'Failed to load quarantined deliveries.' }
    finally { if (!this.destroyed) { this.hasLoaded = true; this.loading = false } }
  }
  recordId(row: Row): string { return row.entities.map((entity) => entity.id).join(', ') || '—' }
  entityTypes(row: Row): string {
    return row.entities.map((entity) => entity.type
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase()))
      .join(', ') || '—'
  }
  statusLabel(status: AtomicDeliveryStatus): string { return status === 'QUARANTINED' ? 'Quarantined' : status === 'ACKNOWLEDGED' ? 'Resolved' : 'Retrying' }
  async retry(row: Row): Promise<void> {
    if (row.status !== 'QUARANTINED' || row.busy) return
    this.confirmation.confirm({
      header: 'Retry delivery', icon: 'pi pi-refresh', message: `Retry Salesforce delivery ${row.deliveryId}?`,
      acceptLabel: 'Retry', rejectLabel: 'Cancel', acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: true }, accept: () => void this.performRetry(row),
    })
  }
  private async performRetry(row: Row): Promise<void> {
    this.replace({ ...row, busy: true, retrying: true })
    try { this.replace({ ...(await this.service.retryAtomicDelivery(row.merchantId, row.deliveryId, { signal: this.abort.signal })), busy: false, retrying: true }) }
    catch (error) { this.error = error instanceof Error ? error.message : 'Failed to retry delivery.'; this.replace({ ...row, busy: false, retrying: false }) }
  }
  async delete(row: Row): Promise<void> {
    if (row.status !== 'QUARANTINED' || row.busy) return
    this.confirmation.confirm({
      header: 'Delete delivery permanently', icon: 'pi pi-trash',
      message: 'Permanently delete this quarantined delivery? It will be removed from the recovery list and cannot be retried.',
      acceptLabel: 'Delete permanently', rejectLabel: 'Cancel', acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: { severity: 'secondary', outlined: true }, accept: () => void this.performDelete(row),
    })
  }
  private async performDelete(row: Row): Promise<void> {
    this.replace({ ...row, busy: true })
    try {
      await this.service.deleteAtomicDelivery(row.merchantId, row.deliveryId, { signal: this.abort.signal })
      this.rows = this.rows.filter((item) => this.key(item) !== this.key(row))
      this.messages.add({ severity: 'success', summary: 'Delivery deleted', detail: 'The quarantined delivery was permanently deleted.' })
    } catch (error) {
      if (error instanceof AtomicDeliveryConflictError) { await this.refresh(); this.messages.add({ severity: 'info', summary: 'Delivery changed', detail: 'This delivery is no longer quarantined.' }) }
      else { this.error = error instanceof Error ? error.message : 'Failed to permanently delete delivery.'; this.replace({ ...row, busy: false }) }
    }
  }
  date(value: string | null | undefined): string { return value ? new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value)) : '—' }
  errorText(value: string | null | undefined): string { return (value || '—').replace(/(bearer\s+)[\w.-]+/gi, '$1[redacted]').replace(/[\r\n]+/g, ' ').slice(0, 500) }
  requestPayload(value: unknown): string {
    if (value === null || value === undefined) return '—'
    return JSON.stringify(value, (key, nested) => /token|secret|password|authorization/i.test(key) ? '[redacted]' : nested, 2)
  }
  highlightedPayload(value: unknown): string {
    const json = this.requestPayload(value)
    const escaped = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    return escaped.replace(/("(?:\\.|[^"\\])*")(?=\s*:)|("(?:\\.|[^"\\])*")|\b(true|false)\b|\b(null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
      (_match, key, string, boolean, nil, number) => key ? `<span class="json-key">${key}</span>` : string ? `<span class="json-string">${string}</span>` : boolean ? `<span class="json-boolean">${boolean}</span>` : nil ? `<span class="json-null">${nil}</span>` : `<span class="json-number">${number}</span>`)
  }
  private payloadsByDelivery(entries: SyncAuditEntry[]): Map<string, unknown> {
    const payloads = new Map<string, unknown>()
    entries.forEach((entry) => {
      const details = entry.details || {}
      const merchantId = typeof details.merchantId === 'string' ? details.merchantId : ''
      const deliveryId = typeof details.deliveryId === 'string' ? details.deliveryId : entry.id.replace('salesforce_atomic_delivery#', '')
      if (merchantId && deliveryId && entry.request_payload !== undefined) payloads.set(`${merchantId}#${deliveryId}`, entry.request_payload)
    })
    return payloads
  }
  private key(row: Pick<Row, 'merchantId' | 'deliveryId'>): string { return `${row.merchantId}#${row.deliveryId}` }
  private replace(row: Row): void { this.rows = this.rows.map((item) => this.key(item) === this.key(row) ? row : item) }
}
